import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./EditEmployee.css";
import apiClient from "../utils/apiClient";

const WEEK_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const EditEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [employeeData, setEmployeeData] = useState({
    email: "",
    name: "",
    phone: "",
    secondary_phone: "",
    emergency_contact: "",
    gender: "",
    joining_date: "",
    dob: "",
    blood_group: "",
    address: "",
    designation: "",
    department: "",
    work_type: "",
    report_to: "",
    role_type: "",
    week_off: [],
    probation_end_date: "",
  });

  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");

  /* ================= FETCH ================= */

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const data = await apiClient(`/auth/employees/${id}/`);

        setEmployeeData({
          ...data,
          // ✅ convert string → array
          week_off:
            typeof data.week_off === "string"
              ? data.week_off.split(",").filter(Boolean)
              : Array.isArray(data.week_off)
              ? data.week_off
              : [],
        });
      } catch (err) {
        console.error(err);
        setError("Failed to fetch employee details");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [id]);

  /* ================= CHANGE ================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (
      ["phone", "secondary_phone", "emergency_contact"].includes(name) &&
      !/^\d*$/.test(value)
    )
      return;

    setEmployeeData((prev) => ({ ...prev, [name]: value }));
  };

  const handleWeekOffChange = (day) => {
    setEmployeeData((prev) => {
      const exists = prev.week_off.includes(day);
      const updated = exists
        ? prev.week_off.filter((d) => d !== day)
        : [...prev.week_off, day];

      return { ...prev, week_off: updated };
    });
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const payload = {
        ...employeeData,
        // ✅ convert array → string
        week_off: employeeData.week_off.join(","),
      };

      await apiClient(`/auth/employees/${id}/`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      setShowSuccess(true);
    } catch (err) {
      if (err?.week_off?.length) {
        setError(err.week_off[0]);
      } else if (err?.detail) {
        setError(err.detail);
      } else {
        setError("Failed to update employee");
      }
    }
  };

  if (loading) return <p>Loading...</p>;

  /* ================= UI ================= */

  return (
    <div className="edit-employee-container">
      <h2>Edit Employee</h2>
      {error && <p className="error-text">{error}</p>}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">

          <input name="email" value={employeeData.email} onChange={handleChange} placeholder="Email" />
          <input name="name" value={employeeData.name} onChange={handleChange} placeholder="Name" />
          <input name="phone" value={employeeData.phone} onChange={handleChange} maxLength="10" placeholder="Phone" />
          <input name="secondary_phone" value={employeeData.secondary_phone} onChange={handleChange} maxLength="10" placeholder="Secondary Phone" />
          <input name="emergency_contact" value={employeeData.emergency_contact} onChange={handleChange} maxLength="10" placeholder="Emergency Contact" />

          <input type="date" name="dob" value={employeeData.dob} onChange={handleChange} />
          <input type="date" name="joining_date" value={employeeData.joining_date} onChange={handleChange} />
          <input type="date" name="probation_end_date" value={employeeData.probation_end_date || ""} onChange={handleChange} />

          <select name="gender" value={employeeData.gender} onChange={handleChange}>
            <option value="">Gender</option>
            <option>Male</option>
            <option>Female</option>
            <option>Other</option>
          </select>

          <select name="blood_group" value={employeeData.blood_group} onChange={handleChange}>
            <option value="">Blood Group</option>
            {["A+","A-","B+","B-","O+","O-","AB+","AB-"].map(bg => (
              <option key={bg}>{bg}</option>
            ))}
          </select>

          <input name="designation" value={employeeData.designation} onChange={handleChange} placeholder="Designation" />
          <input name="report_to" value={employeeData.report_to} onChange={handleChange} placeholder="Report To" />

          <select name="department" value={employeeData.department} onChange={handleChange}>
            <option value="">Department</option>
            <option>Production Support</option>
            <option>Help Desk</option>
            <option>MSSQL DBA</option>
            <option>Development</option>
            <option>HR</option>
            <option>Project Management</option>
            <option>Cloud Support</option>
            <option>Webinar</option>
            <option>DevOps</option>
            <option>Other</option>
          </select>

          <select name="work_type" value={employeeData.work_type} onChange={handleChange}>
            <option value="">Work Type</option>
            <option>Full Time</option>
            <option>Part Time</option>
          </select>

          <select name="role_type" value={employeeData.role_type} onChange={handleChange}>
            <option value="">Role</option>
            <option value="employee">Employee</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>

          <textarea name="address" value={employeeData.address} onChange={handleChange} placeholder="Address" />

          {/* WEEK OFF */}
          <div className="weekoff-box">
            <label>Week Off</label>
            <div className="weekoff-options">
              {WEEK_DAYS.map((day) => (
                <label key={day}>
                  <input
                    type="checkbox"
                    checked={employeeData.week_off.includes(day)}
                    onChange={() => handleWeekOffChange(day)}
                  />
                  {day}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit">Save</button>
          <button type="button" onClick={() => navigate(-1)}>Cancel</button>
        </div>
      </form>

      {showSuccess && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Employee updated successfully</h3>
            <button onClick={() => navigate(-1)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditEmployee;
